// src/http.ts
var IDAHTTPError = class extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "IDAHTTPError";
  }
};
async function request(config, method, path, body) {
  const url = config.apiUrl.replace(/\/$/, "") + path;
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json"
  };
  if (config.apiKey) {
    const isJWT = config.apiKey.startsWith("eyJ");
    headers["Authorization"] = isJWT ? `Bearer ${config.apiKey}` : `ApiKey ${config.apiKey}`;
  }
  const init = {
    method,
    headers,
    signal: config.timeout ? AbortSignal.timeout(config.timeout) : void 0
  };
  if (body !== void 0 && method !== "GET") {
    init.body = JSON.stringify(body);
  }
  const response = await fetch(url, init);
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorCode = "UNKNOWN_ERROR";
    try {
      const errorBody = await response.json();
      if (errorBody.error) {
        errorMessage = errorBody.error.message;
        errorCode = errorBody.error.code;
      }
    } catch {
    }
    throw new IDAHTTPError(response.status, errorCode, errorMessage);
  }
  if (response.status === 204) {
    return void 0;
  }
  const json = await response.json();
  if (json && typeof json === "object" && "data" in json) {
    return json.data;
  }
  return json;
}
async function httpGet(config, path) {
  return request(config, "GET", path);
}
async function httpPost(config, path, body) {
  return request(config, "POST", path, body);
}
async function httpPut(config, path, body) {
  return request(config, "PUT", path, body);
}
async function httpDelete(config, path, body) {
  return request(config, "DELETE", path, body);
}

// src/url.ts
function encodeDID(did) {
  return encodeURIComponent(did).replace(/%3A/gi, ":");
}

// src/marketplace/operations.ts
function withIdem(body, opts) {
  if (!opts?.idempotencyKey) return body;
  return { ...body, __idempotencyKey: opts.idempotencyKey };
}
function qs(params) {
  if (!params) return "";
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === void 0 || v === null) continue;
    if (typeof v === "boolean") {
      sp.set(k, v ? "true" : "false");
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}
var MarketplaceOperations = class {
  constructor(config) {
    this.config = config;
  }
  // ------------------------------------------------------------------
  // Config (no auth, cached) — §3.7
  // ------------------------------------------------------------------
  async getConfig() {
    return httpGet(this.config, "/api/v1/marketplace/config");
  }
  // ------------------------------------------------------------------
  // Listings — §3.1-§3.6
  // ------------------------------------------------------------------
  async listListings(params) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/listings${qs(params)}`
    );
  }
  async getListing(id) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(id)}`
    );
  }
  async createListing(params, opts) {
    return httpPost(
      this.config,
      "/api/v1/marketplace/listings",
      withIdem(params, opts)
    );
  }
  async updateListing(id, params) {
    return httpPut(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(id)}`,
      params
    );
  }
  async deleteListing(id) {
    await httpDelete(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(id)}`
    );
  }
  async pauseListing(id, reason) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(id)}/pause`,
      { reason }
    );
  }
  async resumeListing(id) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(id)}/resume`,
      {}
    );
  }
  // ------------------------------------------------------------------
  // Operator self-service — §4
  // ------------------------------------------------------------------
  async listMyListings(params) {
    return httpGet(
      this.config,
      `/api/v1/operators/me/listings${qs(params)}`
    );
  }
  async getMyEarnings(params) {
    return httpGet(
      this.config,
      `/api/v1/operators/me/earnings${qs(params)}`
    );
  }
  // ------------------------------------------------------------------
  // Quote + Hire — §5
  // ------------------------------------------------------------------
  async quoteHire(listingId, params, opts) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(listingId)}/quote`,
      withIdem(params, opts)
    );
  }
  async hireAgent(listingId, signed, opts) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/listings/${encodeURIComponent(listingId)}/hire`,
      withIdem(signed, opts)
    );
  }
  // ------------------------------------------------------------------
  // Escrow lifecycle — §6
  // ------------------------------------------------------------------
  async listEscrows(params) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/escrows${qs(params)}`
    );
  }
  async getEscrow(escrowId) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}`
    );
  }
  async claimEscrow(escrowId, signed, opts) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}/claim`,
      withIdem(signed, opts)
    );
  }
  async confirmEscrow(escrowId, signed) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}/confirm`,
      signed
    );
  }
  async disputeEscrow(escrowId, signed) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}/dispute`,
      signed
    );
  }
  async resolveEscrow(escrowId, resolveOrder, adjudicatorSignatures) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}/resolve`,
      { resolveOrder, adjudicatorSignatures }
    );
  }
  async timeoutEscrow(escrowId) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}/timeout`,
      {}
    );
  }
  async adjudicatorTimeoutEscrow(escrowId) {
    return httpPost(
      this.config,
      `/api/v1/marketplace/escrows/${encodeURIComponent(escrowId)}/adjudicator-timeout`,
      {}
    );
  }
  // ------------------------------------------------------------------
  // Reputation badges — §7
  // ------------------------------------------------------------------
  async listBadges(params) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/badges${qs(params)}`
    );
  }
  async getBadge(tokenId) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/badges/${encodeURIComponent(tokenId)}`
    );
  }
  /**
   * Convenience accessor — returns the SVG image URL, not the bytes.
   * Browsers fetch the URL directly via `<img src=…>`.
   */
  getBadgeImageURL(tokenId) {
    const base = (this.config.apiUrl || "").replace(/\/$/, "");
    return `${base}/api/v1/marketplace/badges/${encodeURIComponent(tokenId)}/image.svg`;
  }
  async listAgentBadges(agentDid, params) {
    return httpGet(
      this.config,
      `/api/v1/marketplace/badges${qs({ ...params, agentDid })}`
    );
  }
  // ------------------------------------------------------------------
  // Attestations — §8
  // ------------------------------------------------------------------
  async issueAttestation(agentDid, params, opts) {
    return httpPost(
      this.config,
      `/api/v1/agents/${encodeDID(agentDid)}/attestations`,
      withIdem(params, opts)
    );
  }
  async listAgentAttestations(agentDid, params) {
    return httpGet(
      this.config,
      `/api/v1/agents/${encodeDID(agentDid)}/attestations${qs(params)}`
    );
  }
  async revokeAttestation(agentDid, attestationId, reason) {
    const query = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    await httpDelete(
      this.config,
      `/api/v1/agents/${encodeDID(agentDid)}/attestations/${encodeURIComponent(attestationId)}${query}`
    );
  }
  // ------------------------------------------------------------------
  // Treasury admin — §9
  // ------------------------------------------------------------------
  async getTreasuryState() {
    return httpGet(this.config, "/api/v1/admin/marketplace/treasury");
  }
  async proposeTreasury(params) {
    return httpPost(this.config, "/api/v1/admin/marketplace/treasury/propose", params);
  }
  async activateTreasury() {
    return httpPost(this.config, "/api/v1/admin/marketplace/treasury/activate", {});
  }
  // ------------------------------------------------------------------
  // Wallet sub-key — §10
  // ------------------------------------------------------------------
  async getMarketplacePubkey() {
    return httpGet(this.config, "/api/v1/wallets/me/marketplace-pubkey");
  }
  async rotateMarketplacePubkey() {
    return httpPost(this.config, "/api/v1/wallets/me/secp-key/rotate", {});
  }
  /**
   * Server-mediated EIP-712 signing — the API holds the wallet's secp256k1
   * sub-key in MPC custody, so the portal POSTs the typed-data to a sign
   * endpoint and gets back a 65-byte ECDSA signature. The on-chain contract
   * then verifies via the standard `_hashTypedDataV4 + ECDSA.recover` flow.
   *
   * `typedData` MUST be the output of one of the `buildXxxTypedData` helpers
   * — the API enforces a strict allow-list of `primaryType` values to avoid
   * being a generic signing oracle.
   */
  async signEIP712(typedData) {
    return httpPost(
      this.config,
      "/api/v1/wallets/me/sign-eip712",
      { typedData }
    );
  }
  // ------------------------------------------------------------------
  // SDK 1.5.0 — Phase 7-D additions (mobile wallet marketplace)
  //
  // `walletSummary` and `escrowTimeline` are read-only aggregates the wallet
  // calls on tab focus / pull-to-refresh. `rotateSubkey` is the wallet-side
  // rotation distinct from the portal's `/secp-key/rotate`.
  //
  // The two handover endpoints + status read are exposed so the wallet's
  // `core/subkey/handover.ts` orchestrator can drive the protocol from
  // `01_architecture.md §4.2`.
  // ------------------------------------------------------------------
  async walletSummary(params) {
    const path = `/api/v1/marketplace/wallet/${encodeDID(params.did)}/summary${qs({
      windowDays: params.windowDays
    })}`;
    return httpGet(this.config, path);
  }
  async escrowTimeline(params) {
    const path = `/api/v1/marketplace/escrows/${encodeURIComponent(params.escrowId)}/timeline${qs({
      limit: params.limit,
      cursor: params.cursor
    })}`;
    return httpGet(this.config, path);
  }
  async rotateSubkey(input, opts) {
    return httpPost(
      this.config,
      "/api/v1/wallet/marketplace/rotate-subkey",
      withIdem(input, opts)
    );
  }
  async requestSubkeyHandover(input, opts) {
    return httpPost(
      this.config,
      "/api/v1/wallet/marketplace-subkey/handover/request",
      withIdem(input, opts)
    );
  }
  async ackSubkeyHandover(input, opts) {
    return httpPost(
      this.config,
      "/api/v1/wallet/marketplace-subkey/handover/ack",
      withIdem(input, opts)
    );
  }
  async subkeyStatus() {
    return httpGet(
      this.config,
      "/api/v1/wallet/marketplace-subkey/status"
    );
  }
};

// src/marketplace/eip712.ts
import { keccak_256 } from "@noble/hashes/sha3";
var EIP712_DOMAIN_TYPE = [
  { name: "name", type: "string" },
  { name: "version", type: "string" },
  { name: "chainId", type: "uint256" },
  { name: "verifyingContract", type: "address" }
];
function domainFromConfig(config) {
  if (config.domain) {
    return {
      name: config.domain.name,
      version: config.domain.version,
      chainId: config.domain.chainId,
      verifyingContract: config.domain.verifyingContract
    };
  }
  return {
    name: "IDA-MarketplaceEscrow",
    version: "1",
    chainId: config.chainId,
    verifyingContract: config.marketplaceEscrowAddress
  };
}
var HIRE_ORDER_TYPE = [
  { name: "buyer", type: "address" },
  { name: "listingId", type: "bytes32" },
  { name: "priceWei", type: "uint256" },
  { name: "feeBps", type: "uint256" },
  { name: "ibctScope", type: "string" },
  { name: "ibctTtlSeconds", type: "uint256" },
  { name: "nonce", type: "bytes32" },
  { name: "deadline", type: "uint256" }
];
var CONFIRM_RECEIPT_TYPE = [
  { name: "buyer", type: "address" },
  { name: "escrowId", type: "bytes32" },
  { name: "deliveryDigest", type: "bytes32" },
  { name: "deadline", type: "uint256" },
  { name: "nonce", type: "bytes32" }
];
var DISPUTE_RECEIPT_TYPE = [
  { name: "buyer", type: "address" },
  { name: "escrowId", type: "bytes32" },
  { name: "reason", type: "string" },
  { name: "evidenceCid", type: "bytes32" },
  { name: "deadline", type: "uint256" },
  { name: "nonce", type: "bytes32" }
];
var CLAIM_RECEIPT_TYPE = [
  { name: "operator", type: "address" },
  { name: "escrowId", type: "bytes32" },
  { name: "deliveryDigest", type: "bytes32" },
  { name: "deadline", type: "uint256" },
  { name: "nonce", type: "bytes32" }
];
var RESOLVE_ORDER_TYPE = [
  { name: "escrowId", type: "bytes32" },
  { name: "side", type: "uint8" },
  { name: "buyerShareBps", type: "uint256" },
  { name: "rationale", type: "string" },
  { name: "deadline", type: "uint256" },
  { name: "nonce", type: "bytes32" }
];
var ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;
var BYTES32_RE = /^0x[0-9a-fA-F]{64}$/;
function assertDomain(domain) {
  if (domain.name !== "IDA-MarketplaceEscrow") {
    throw new Error(
      `EIP712: invalid domain.name "${domain.name}" (expected "IDA-MarketplaceEscrow")`
    );
  }
  if (domain.version !== "1") {
    throw new Error(`EIP712: invalid domain.version "${domain.version}" (expected "1")`);
  }
  if (!Number.isFinite(domain.chainId) || domain.chainId <= 0) {
    throw new Error(`EIP712: invalid domain.chainId ${domain.chainId}`);
  }
  if (!ADDRESS_RE.test(domain.verifyingContract)) {
    throw new Error(`EIP712: invalid domain.verifyingContract "${domain.verifyingContract}"`);
  }
}
function requirePresent(obj, field, ctx) {
  const v = obj[field];
  if (v === void 0 || v === null) {
    throw new Error(`EIP712 ${ctx}: missing required field "${field}"`);
  }
}
function requireBytes32(obj, field, ctx) {
  requirePresent(obj, field, ctx);
  const v = obj[field];
  if (typeof v !== "string" || !BYTES32_RE.test(v)) {
    throw new Error(`EIP712 ${ctx}: field "${field}" must be 0x-prefixed 32-byte hex`);
  }
}
function requireAddress(obj, field, ctx) {
  requirePresent(obj, field, ctx);
  const v = obj[field];
  if (typeof v !== "string" || !ADDRESS_RE.test(v)) {
    throw new Error(`EIP712 ${ctx}: field "${field}" must be a 0x-prefixed Ethereum address`);
  }
}
function buildHireOrderTypedData(order, domain) {
  assertDomain(domain);
  requireAddress(order, "buyer", "HireOrder");
  requireBytes32(order, "listingId", "HireOrder");
  requirePresent(order, "priceWei", "HireOrder");
  requirePresent(order, "feeBps", "HireOrder");
  requirePresent(order, "ibctScope", "HireOrder");
  requirePresent(order, "ibctTtlSeconds", "HireOrder");
  requireBytes32(order, "nonce", "HireOrder");
  requirePresent(order, "deadline", "HireOrder");
  return {
    domain,
    types: {
      EIP712Domain: EIP712_DOMAIN_TYPE,
      HireOrder: HIRE_ORDER_TYPE
    },
    primaryType: "HireOrder",
    message: {
      buyer: order.buyer,
      listingId: order.listingId,
      priceWei: order.priceWei,
      feeBps: order.feeBps,
      ibctScope: order.ibctScope,
      ibctTtlSeconds: order.ibctTtlSeconds,
      nonce: order.nonce,
      deadline: order.deadline
    }
  };
}
function buildConfirmReceiptTypedData(receipt, domain) {
  assertDomain(domain);
  requireAddress(receipt, "buyer", "ConfirmReceipt");
  requireBytes32(receipt, "escrowId", "ConfirmReceipt");
  requireBytes32(receipt, "deliveryDigest", "ConfirmReceipt");
  requirePresent(receipt, "deadline", "ConfirmReceipt");
  requireBytes32(receipt, "nonce", "ConfirmReceipt");
  return {
    domain,
    types: {
      EIP712Domain: EIP712_DOMAIN_TYPE,
      ConfirmReceipt: CONFIRM_RECEIPT_TYPE
    },
    primaryType: "ConfirmReceipt",
    message: {
      buyer: receipt.buyer,
      escrowId: receipt.escrowId,
      deliveryDigest: receipt.deliveryDigest,
      deadline: receipt.deadline,
      nonce: receipt.nonce
    }
  };
}
function buildDisputeReceiptTypedData(receipt, domain) {
  assertDomain(domain);
  requireAddress(receipt, "buyer", "DisputeReceipt");
  requireBytes32(receipt, "escrowId", "DisputeReceipt");
  requirePresent(receipt, "reason", "DisputeReceipt");
  requireBytes32(receipt, "evidenceCid", "DisputeReceipt");
  requirePresent(receipt, "deadline", "DisputeReceipt");
  requireBytes32(receipt, "nonce", "DisputeReceipt");
  return {
    domain,
    types: {
      EIP712Domain: EIP712_DOMAIN_TYPE,
      DisputeReceipt: DISPUTE_RECEIPT_TYPE
    },
    primaryType: "DisputeReceipt",
    message: {
      buyer: receipt.buyer,
      escrowId: receipt.escrowId,
      reason: receipt.reason,
      evidenceCid: receipt.evidenceCid,
      deadline: receipt.deadline,
      nonce: receipt.nonce
    }
  };
}
function buildClaimReceiptTypedData(receipt, domain) {
  assertDomain(domain);
  requireAddress(receipt, "operator", "ClaimReceipt");
  requireBytes32(receipt, "escrowId", "ClaimReceipt");
  requireBytes32(receipt, "deliveryDigest", "ClaimReceipt");
  requirePresent(receipt, "deadline", "ClaimReceipt");
  requireBytes32(receipt, "nonce", "ClaimReceipt");
  return {
    domain,
    types: {
      EIP712Domain: EIP712_DOMAIN_TYPE,
      ClaimReceipt: CLAIM_RECEIPT_TYPE
    },
    primaryType: "ClaimReceipt",
    message: {
      operator: receipt.operator,
      escrowId: receipt.escrowId,
      deliveryDigest: receipt.deliveryDigest,
      deadline: receipt.deadline,
      nonce: receipt.nonce
    }
  };
}
function buildResolveOrderTypedData(order, domain) {
  assertDomain(domain);
  requireBytes32(order, "escrowId", "ResolveOrder");
  if (order.side !== 0 && order.side !== 1 && order.side !== 2) {
    throw new Error(`EIP712 ResolveOrder: side must be 0 (buyer), 1 (operator), or 2 (split); got ${order.side}`);
  }
  requirePresent(order, "buyerShareBps", "ResolveOrder");
  requirePresent(order, "rationale", "ResolveOrder");
  requirePresent(order, "deadline", "ResolveOrder");
  requireBytes32(order, "nonce", "ResolveOrder");
  return {
    domain,
    types: {
      EIP712Domain: EIP712_DOMAIN_TYPE,
      ResolveOrder: RESOLVE_ORDER_TYPE
    },
    primaryType: "ResolveOrder",
    message: {
      escrowId: order.escrowId,
      side: order.side,
      buyerShareBps: order.buyerShareBps,
      rationale: order.rationale,
      deadline: order.deadline,
      nonce: order.nonce
    }
  };
}
var TYPED_DATA_FIELDS = {
  EIP712Domain: EIP712_DOMAIN_TYPE,
  HireOrder: HIRE_ORDER_TYPE,
  ConfirmReceipt: CONFIRM_RECEIPT_TYPE,
  DisputeReceipt: DISPUTE_RECEIPT_TYPE,
  ClaimReceipt: CLAIM_RECEIPT_TYPE,
  ResolveOrder: RESOLVE_ORDER_TYPE
};
var HEX_RE = /^0x[0-9a-fA-F]*$/;
function utf8ToBytes(s) {
  return new TextEncoder().encode(s);
}
function hexToBytes(hex) {
  if (!HEX_RE.test(hex) || hex.length % 2 !== 0) {
    throw new Error(`EIP712: invalid hex string "${hex}"`);
  }
  const out = new Uint8Array((hex.length - 2) / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(2 + i * 2, 4 + i * 2), 16);
  }
  return out;
}
function bytesToHex(b) {
  let s = "";
  for (let i = 0; i < b.length; i++) s += b[i].toString(16).padStart(2, "0");
  return s;
}
function concat(...arrs) {
  let total = 0;
  for (const a of arrs) total += a.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrs) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}
function leftPad32(b) {
  if (b.length === 32) return b;
  if (b.length > 32) throw new Error("EIP712: word > 32 bytes");
  const out = new Uint8Array(32);
  out.set(b, 32 - b.length);
  return out;
}
function uintToBytes32(value) {
  let n;
  if (typeof value === "bigint") n = value;
  else if (typeof value === "number") {
    if (!Number.isFinite(value) || !Number.isInteger(value)) {
      throw new Error(`EIP712: uint must be integer (got ${value})`);
    }
    n = BigInt(value);
  } else if (typeof value === "string") {
    if (value.startsWith("0x") || value.startsWith("0X")) {
      n = BigInt(value);
    } else {
      n = BigInt(value);
    }
  } else {
    throw new Error(`EIP712: uint expected string|number|bigint`);
  }
  if (n < 0n) throw new Error("EIP712: uint must be non-negative");
  const max = (1n << 256n) - 1n;
  if (n > max) throw new Error("EIP712: uint exceeds 2^256-1");
  const out = new Uint8Array(32);
  for (let i = 31; i >= 0; i--) {
    out[i] = Number(n & 0xffn);
    n >>= 8n;
  }
  return out;
}
function intToBytes32(value, bits) {
  let n;
  if (typeof value === "bigint") n = value;
  else if (typeof value === "number") n = BigInt(value);
  else n = BigInt(value);
  const range = 1n << BigInt(bits);
  if (n < 0n) n = range + n;
  if (n < 0n || n >= range) throw new Error(`EIP712: int${bits} out of range`);
  const out = new Uint8Array(32);
  for (let i = 31; i >= 0; i--) {
    out[i] = Number(n & 0xffn);
    n >>= 8n;
  }
  return out;
}
function encodeField(types, type, value) {
  if (type.endsWith("[]")) {
    if (!Array.isArray(value)) throw new Error(`EIP712: ${type} expects array`);
    const inner = type.slice(0, -2);
    const concatBytes = concat(
      ...value.map((v) => encodeField(types, inner, v))
    );
    return keccak_256(concatBytes);
  }
  const fixedMatch = /^(.*)\[(\d+)\]$/.exec(type);
  if (fixedMatch) {
    if (!Array.isArray(value)) throw new Error(`EIP712: ${type} expects array`);
    const len = Number(fixedMatch[2]);
    if (value.length !== len) throw new Error(`EIP712: ${type} length \u2260 ${len}`);
    const concatBytes = concat(
      ...value.map((v) => encodeField(types, fixedMatch[1], v))
    );
    return keccak_256(concatBytes);
  }
  if (type === "string") {
    return keccak_256(utf8ToBytes(String(value)));
  }
  if (type === "bytes") {
    if (typeof value !== "string" || !HEX_RE.test(value)) {
      throw new Error(`EIP712: bytes must be 0x-prefixed hex`);
    }
    return keccak_256(hexToBytes(value));
  }
  if (type === "address") {
    if (typeof value !== "string" || !ADDRESS_RE.test(value)) {
      throw new Error(`EIP712: address must be 0x-prefixed 20-byte hex`);
    }
    return leftPad32(hexToBytes(value));
  }
  if (type === "bool") {
    return uintToBytes32(value ? 1n : 0n);
  }
  const bytesNMatch = /^bytes(\d+)$/.exec(type);
  if (bytesNMatch) {
    const n = Number(bytesNMatch[1]);
    if (n < 1 || n > 32) throw new Error(`EIP712: bytesN out of range (${n})`);
    if (typeof value !== "string" || !HEX_RE.test(value)) {
      throw new Error(`EIP712: ${type} must be 0x-prefixed hex`);
    }
    const b = hexToBytes(value);
    if (b.length !== n) {
      throw new Error(`EIP712: ${type} expected ${n} bytes, got ${b.length}`);
    }
    const out = new Uint8Array(32);
    out.set(b, 0);
    return out;
  }
  const uintMatch = /^uint(\d*)$/.exec(type);
  if (uintMatch) {
    return uintToBytes32(value);
  }
  const intMatch = /^int(\d+)$/.exec(type);
  if (intMatch) {
    return intToBytes32(value, Number(intMatch[1]));
  }
  if (types[type]) {
    return hashStructInternal(types, type, value);
  }
  throw new Error(`EIP712: unsupported type "${type}"`);
}
function encodeType(types, primaryType) {
  const deps = /* @__PURE__ */ new Set();
  const visit = (typeName) => {
    if (deps.has(typeName) || !types[typeName]) return;
    deps.add(typeName);
    for (const f of types[typeName]) {
      const baseType = f.type.replace(/\[\d*\]$/, "");
      if (types[baseType]) visit(baseType);
    }
  };
  visit(primaryType);
  deps.delete(primaryType);
  const ordered = [primaryType, ...Array.from(deps).sort()];
  return ordered.map((t) => `${t}(${types[t].map((f) => `${f.type} ${f.name}`).join(",")})`).join("");
}
function typeHash(types, primaryType) {
  return keccak_256(utf8ToBytes(encodeType(types, primaryType)));
}
function hashStructInternal(types, primaryType, data) {
  const fields = types[primaryType];
  if (!fields) throw new Error(`EIP712: unknown struct "${primaryType}"`);
  const parts = [typeHash(types, primaryType)];
  for (const f of fields) {
    parts.push(encodeField(types, f.type, data[f.name]));
  }
  return keccak_256(concat(...parts));
}
function hashDomain(domain) {
  return hashStructInternal(
    { EIP712Domain: EIP712_DOMAIN_TYPE },
    "EIP712Domain",
    domain
  );
}
function hashStruct(typedData) {
  return hashStructInternal(
    typedData.types,
    typedData.primaryType,
    typedData.message
  );
}
function hashTypedData(typedData) {
  const ds = hashDomain(typedData.domain);
  const sh = hashStruct(typedData);
  const buf = new Uint8Array(2 + 32 + 32);
  buf[0] = 25;
  buf[1] = 1;
  buf.set(ds, 2);
  buf.set(sh, 34);
  return keccak_256(buf);
}
function hashTypedDataHex(typedData) {
  return "0x" + bytesToHex(hashTypedData(typedData));
}

export {
  httpGet,
  httpPost,
  httpPut,
  httpDelete,
  encodeDID,
  MarketplaceOperations,
  domainFromConfig,
  buildHireOrderTypedData,
  buildConfirmReceiptTypedData,
  buildDisputeReceiptTypedData,
  buildClaimReceiptTypedData,
  buildResolveOrderTypedData,
  TYPED_DATA_FIELDS,
  encodeType,
  hashDomain,
  hashStruct,
  hashTypedData,
  hashTypedDataHex
};
